import { Alert, Stack, Typography } from "@mui/material";
import { isComgateProductCode } from "Components/Shared/Merchant/Products/AppProducts/constants";
import { useGetMerchantQuery } from "Api/Queries/Merchants/useGetMerchantQuery";
import { MerchantAvatarUpload } from "Components/Hunter/Merchants/Detail/MerchantAvatarUpload";
import { StepBox } from "Components/Onboarding/shared/StepBox";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { InfoBox } from "Components/Shared/InfoBox/InfoBox";
import { Facilities } from "Components/Shared/Merchant/Facilities/Facilities";
import { isFacilityComplete } from "Components/Shared/Merchant/Facilities/utils";
import { useGetMerchantAppProductsQuery } from "Api/Queries/Merchants/useGetMerchantAppProductsQuery";
import { AddressType } from "Infrastructure/Api/Api";
import { getRegionCurrency } from "Utils/CurrencyUtils";
import { Resources, useResource } from "Translations/Resources";

const StepResources =
  Resources.Onboarding.BusinessOnboarding.Steps.FacilityCompletion;

type Props = {
  onNextStepRequested: () => void;
};

export const FacilityCompletionStep = ({ onNextStepRequested }: Props) => {
  const { data: merchantData, isLoading: isMerchantLoading } =
    useGetMerchantQuery();
  const { data: appProducts, isLoading: isAppProductsLoading } =
    useGetMerchantAppProductsQuery();
  const { t } = useResource();

  const hasComgateProduct = !!appProducts?.some((ap) =>
    isComgateProductCode(ap.productDto?.code),
  );

  if (isMerchantLoading || isAppProductsLoading) {
    return <CenteredCircularProgress />;
  }

  if (!merchantData) {
    return (
      <Alert severity="error">{t(Resources.Errors.DataLoadingError)}</Alert>
    );
  }

  const companyHeadquartersAddress = merchantData.party?.addresses?.find(
    ({ type }) => type === AddressType.Headquarters,
  );

  const hasFacilities = merchantData.facilities.length > 0;
  const allFacilitiesComplete =
    hasFacilities && merchantData.facilities.every(isFacilityComplete);
  const isFacilityRequired = !!hasComgateProduct;
  const isSubmitDisabled =
    (isFacilityRequired && !hasFacilities) ||
    (hasFacilities && !allFacilitiesComplete);

  return (
    <StepBox
      submitButtonLabel={
        hasFacilities || isFacilityRequired
          ? t(Resources.Common.Continue)
          : t(StepResources.ContinueWithoutFacility)
      }
      submitDisabled={isSubmitDisabled}
      submitButtonDescription={
        isSubmitDisabled ? (
          <InfoBox
            variant="warning"
            content={
              isFacilityRequired && !hasFacilities
                ? t(StepResources.OrbitLFacilityRequiredWarning)
                : t(StepResources.IncompleteFacilitiesWarning)
            }
          />
        ) : undefined
      }
      onSubmitClick={onNextStepRequested}
    >
      <Stack gap={3}>
        <Typography>{t(StepResources.Description)}</Typography>
        <MerchantAvatarUpload merchantPublicID={merchantData.publicID} />
        <Facilities
          isOnboarding
          merchantPublicID={merchantData.publicID}
          facilities={merchantData.facilities}
          companyHeadquartersAddress={companyHeadquartersAddress}
          partyPublicID={merchantData.party?.publicID}
          marketCurrencyCode={
            merchantData.party?.region
              ? getRegionCurrency(merchantData.party.region)
              : undefined
          }
        />
      </Stack>
    </StepBox>
  );
};
